import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { getCategoryList, createCategory, updateCategory, deleteCategory, toggleCategoryDisplay } from '../../api/category';

const LEVEL_LABEL = { 1: '대분류', 2: '중분류', 3: '소분류' };

export default function CategoryManagePage() {
  const { auth } = useAuth();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedLarge, setSelectedLarge] = useState(null);
  const [selectedMedium, setSelectedMedium] = useState(null);

  const [modalLevel, setModalLevel] = useState(null); // 생성할 레벨 (1/2/3)
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getCategoryList();
      setAll(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || '분류 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const larges = all.filter((c) => c.categoryLevel === 1);
  const mediums = all.filter((c) => c.categoryLevel === 2 && c.parentCategoryId === selectedLarge?.categoryId);
  const smalls = all.filter((c) => c.categoryLevel === 3 && c.parentCategoryId === selectedMedium?.categoryId);

  const handleSelectLarge = (c) => {
    setSelectedLarge(c);
    setSelectedMedium(null);
  };

  const handleSave = async (form) => {
    try {
      if (editing) {
        await updateCategory(editing.categoryId, { ...form, updatedBy: auth.adminName });
      } else {
        await createCategory({ ...form, createdBy: auth.adminName });
      }
      setModalLevel(null);
      setEditing(null);
      fetchAll();
    } catch (err) {
      window.alert(err.response?.data?.message || '저장에 실패했습니다.');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCategory(deleteTarget.categoryId);
      setDeleteTarget(null);
      fetchAll();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('분류 삭제 실패:', err.response?.status, err.response?.data, err);
      const serverMessage = err.response?.data?.message;
      window.alert(
        serverMessage
          ? serverMessage
          : `삭제에 실패했습니다. (서버 응답 코드: ${err.response?.status ?? '알 수 없음'} - 브라우저 개발자도구 콘솔/네트워크 탭에서 자세한 내용을 확인하세요)`
      );
    }
  };

  const handleToggleDisplay = async (category) => {
    const next = category.displayYn === 'Y' ? 'N' : 'Y';
    const label = next === 'Y' ? '노출' : '비노출';
    if (!window.confirm(`'${category.categoryName}' 분류의 상품 전체를 ${label} 처리하시겠습니까?`)) return;
    try {
      await toggleCategoryDisplay(category.categoryId, next, auth.adminName);
      window.alert(`상품 전체가 ${label} 처리되었습니다.`);
      fetchAll();
    } catch (err) {
      window.alert(err.response?.data?.message || '처리에 실패했습니다.');
    }
  };

  const renderColumn = (title, level, items, selected, onSelect, parentRequired) => (
    <div className="card" style={{ flex: 1 }}>
      <div className="flex-between" style={{ padding: '14px 16px', borderBottom: '1px solid var(--color-border-light)' }}>
        <strong style={{ fontSize: 13 }}>{title}</strong>
        <button
          className="btn btn-ghost btn-sm"
          disabled={parentRequired && !parentRequired.ok}
          onClick={() => { setEditing(null); setModalLevel(level); }}
        >
          + 추가
        </button>
      </div>
      <div className="pane-list">
        {loading && <div className="empty-row">불러오는 중...</div>}
        {!loading && parentRequired && !parentRequired.ok && <div className="empty-row">{parentRequired.message}</div>}
        {!loading && (!parentRequired || parentRequired.ok) && items.length === 0 && <div className="empty-row">등록된 분류가 없습니다.</div>}
        {!loading && items.map((c) => (
          <div
            key={c.categoryId}
            className={`pane-list-item${selected?.categoryId === c.categoryId ? ' selected' : ''}`}
            onClick={() => onSelect && onSelect(c)}
          >
            <div>{c.categoryName}</div>
            <div className="actions" onClick={(e) => e.stopPropagation()}>
              {level === 3 && (
                <button
                  className={`btn btn-sm ${c.displayYn === 'Y' ? 'btn-secondary' : 'btn-primary'}`}
                  onClick={() => handleToggleDisplay(c)}
                  title="이 분류의 상품 전체 노출/비노출"
                >
                  {c.displayYn === 'Y' ? '노출중' : '비노출'}
                </button>
              )}
              <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(c); setModalLevel(level); }}>수정</button>
              <button className="btn btn-danger btn-sm" onClick={() => setDeleteTarget(c)}>삭제</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <AdminLayout title="상품분류관리" menuUrl="/admin/category">
      <div className="page-header">
        <div>
          <h1>상품분류관리</h1>
          <div className="subtitle">대분류 → 중분류 → 소분류 3단계로 상품을 분류합니다. 소분류 단위로 상품 전체 노출/비노출을 일괄 처리할 수 있습니다.</div>
        </div>
      </div>

      {error && <div className="card card-padded" style={{ marginBottom: 20 }}><span className="text-danger">{error}</span></div>}

      <div style={{ display: 'flex', gap: 16 }}>
        {renderColumn('대분류', 1, larges, selectedLarge, handleSelectLarge, null)}
        {renderColumn('중분류', 2, mediums, selectedMedium, setSelectedMedium,
          { ok: !!selectedLarge, message: '왼쪽에서 대분류를 먼저 선택하세요.' })}
        {renderColumn('소분류', 3, smalls, null, null,
          { ok: !!selectedMedium, message: '중분류를 먼저 선택하세요.' })}
      </div>

      {modalLevel && (
        <CategoryFormModal
          level={modalLevel}
          category={editing}
          parentCategoryId={modalLevel === 2 ? selectedLarge?.categoryId : modalLevel === 3 ? selectedMedium?.categoryId : null}
          onClose={() => { setModalLevel(null); setEditing(null); }}
          onSave={handleSave}
        />
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="분류 삭제"
          message={`'${deleteTarget.categoryName}' 분류를 삭제하시겠습니까?`}
          confirmLabel="삭제"
          danger
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
    </AdminLayout>
  );
}

function CategoryFormModal({ level, category, parentCategoryId, onClose, onSave }) {
  const isNew = !category;
  const [form, setForm] = useState({
    categoryName: category?.categoryName || '',
    sortOrder: category?.sortOrder ?? 0,
    displayYn: category?.displayYn || 'Y',
    useYn: category?.useYn || 'Y',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({ ...form, categoryLevel: level, parentCategoryId, sortOrder: Number(form.sortOrder) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`${isNew ? '추가' : '수정'} - ${LEVEL_LABEL[level]}`}
      onClose={onClose}
      width={360}
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>취소</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
      </>}
    >
      <form onSubmit={submit}>
        <div className="field">
          <label>분류명</label>
          <input className="input" value={form.categoryName} onChange={(e) => setForm({ ...form, categoryName: e.target.value })} required />
        </div>
        <div className="field">
          <label>정렬순서</label>
          <input className="input" type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} />
        </div>
        <div className="field checkbox-row">
          <input type="checkbox" id="catUseYn" checked={form.useYn === 'Y'} onChange={(e) => setForm({ ...form, useYn: e.target.checked ? 'Y' : 'N' })} />
          <label htmlFor="catUseYn" style={{ margin: 0 }}>사용함</label>
        </div>
      </form>
    </Modal>
  );
}
